import React, { useState, Fragment } from 'react'
import * as R from 'ramda'
import effects from 'app/effects/effects'
import PropTypes from 'prop-types'
import MessageAlert from 'app/components/MessageAlert'
import { errorTypes } from 'commons/error'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { humanizedTimeIndicator } from 'libs/humanizedTimeIndicator/humanizedTimeIndicator'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  TextField,
  Tooltip,
} from '@material-ui/core'
import Formsy from 'formsy-react'
import { useIntl, FormattedMessage } from 'react-intl'
import themesConfig from 'app/fuse-configs/themesConfig'
import clsx from 'clsx'
import CommentReplyMenu from 'app/main/messages/notificationTimeline/commentReplay/CommentReplyMenu'
import { useIsPaidPlan } from 'app/auth/paidPlanAccess'
import PersonAvatar from './PersonAvatar'

const useStyles = makeStyles((theme) => ({
  editingField: {
    '& .MuiInputBase-input.MuiInput-input.MuiInputBase-inputMultiline.MuiInput-inputMultiline':
      {
        fontSize: '14px',
      },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: '#fff',
    },
  },
  submitEdit: {
    fontSize: '1rem',
  },
  avatarIcon: {
    width: '32px',
    height: '32px',
    fontSize: '14px',
  },
  editIcon: {
    color: '#a0a0a0',
    display: 'flex',
    opacity: '0',
    visibility: 'hidden',
    alignSelf: 'center',
    position: 'relative',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  commentBoxcontainer: {
    display: 'flex',
    margin: '16px 0',
    '&:hover :nth-child(4)': {
      display: 'flex',
      opacity: '1',
      visibility: 'visible',
    },
  },
  commentBoxHide: {
    '&:hover :nth-child(4)': {
      display: 'flex',
      opacity: '0',
      visibility: 'hidden',
      marginLeft: '10px',
    },
  },
  commentTextfield: {
    width: 'auto',
    color: '#000000a8',
    fontSize: '9px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  defaultComment: {
    fontSize: '14px',
    color: theme.palette.darkGrey.main,
    margin: '5px 0',
  },
}))

const ReplyCard = ({
  userId,
  onReplyUpdateSuccess,
  reply,
  handleDeleteReply,
  notificationId,
}) => {
  const [isOpenDeletingDialog, setIsOpenDeletingDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [updatedText, setUpdatedText] = useState('')
  const [isSubmitEditing, setIsSubmitEditing] = useState(false)

  const [isEditBoxOpen, setIsEditBoxOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const defaultLanguage = useDefaultLanguage()
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const creatorName = R.path(['creator', 'name'], reply)
  const creatorId = R.path(['creator', 'id'], reply)
  const replyUserId = userId
  const isCreator = creatorId === replyUserId
  const createAt = humanizedTimeIndicator({
    timestamp: R.prop('createdAt', reply),
    languageCode: R.prop('code', defaultLanguage),
    formatMessage: formatMessage,
  })

  const isPaidPlan = useIsPaidPlan()

  const handleClose = () => {
    setIsOpenDeletingDialog(false)
  }

  const editCommentBoxClose = () => {
    setIsEditBoxOpen(null)
  }

  const activeEditing = () => {
    setIsEditing(true)
    setIsEditBoxOpen(null)
  }

  const activeDeleting = () => {
    setIsEditBoxOpen(null)
    setIsOpenDeletingDialog(true)
  }

  const handleEditPopover = (e) => {
    setAnchorEl(e.currentTarget)
    setIsEditBoxOpen(true)
  }

  const handleDeleteItem = () => {
    const commentId = reply.id
    handleDeleteReply(commentId)
    setIsOpenDeletingDialog(false)
  }

  function handleEnterToEdit(e) {
    const ENTER_KEY = 'Enter'
    if (e.key === ENTER_KEY && e.shiftKey) {
      return
    } else if (e.key === ENTER_KEY) {
      e.preventDefault()
      validateEmptyEditing()
    }
  }

  function validateEmptyEditing() {
    if (R.compose(R.not, R.anyPass([R.isNil, R.isEmpty]))(updatedText)) {
      submitEditing()
    } else {
      cancelEditing()
    }
  }

  const submitEditing = () => {
    setIsEditing(false)
    setIsSubmitEditing(false)
    effects.apis
      .updateComment({
        id: R.prop('id', reply),
        text: updatedText,
      })
      .then(onReplyUpdateSuccess)
      .catch((error) => {
        return dispatch(
          MessageAlert.showNetworkError(formatMessage)(
            error,
            errorTypes.default,
          ),
        )
      })
      .finally(() => {
        setIsSubmitEditing(false)
      })
  }

  function cancelEditing() {
    setIsEditing(false)
    setUpdatedText(reply.text)
  }

  return (
    <Fragment>
      <div
        className={
          isEditing
            ? clsx(classes.commentBoxHide)
            : clsx(classes.commentBoxcontainer)
        }
      >
        <PersonAvatar
          placeHolder={R.path(['creator', 'avatarCharacters'], reply)}
          subId={reply.creator.id}
          alt={reply.creator.name}
          src={reply.creator.avatar}
          className={clsx(classes.avatarIcon, 'ltr:mr-5 rtl:ml-5')}
        />
        <div className={isEditing ? 'hidden' : clsx(classes.commentTextfield)}>
          <span>
            {formatMessage(
              { id: 'notificationTimeline.card.comment.creator.name' },
              { creatorName, createAt },
            )}
          </span>
          <p className={clsx(classes.defaultComment)}>
            {updatedText || reply.text}
          </p>
        </div>
        <Formsy
          className={isEditing ? 'w-full' : 'hidden'}
          onKeyPress={handleEnterToEdit}
        >
          <TextField
            label={formatMessage(
              { id: 'notificationTimeline.card.reply.creator.name' },
              { creatorName, createAt },
            )}
            defaultValue={reply.text}
            value={R.isEmpty(updatedText) ? reply.text : updatedText}
            className={clsx(classes.editingField, 'w-full')}
            margin="normal"
            multiline
            variant="outlined"
            onChange={(e) => setUpdatedText(e.target.value)}
            disabled={isSubmitEditing}
          />
        </Formsy>
        <Tooltip title="Edit or delete this" arrow="true">
          <Icon
            onClick={(e) => (!isPaidPlan ? null : handleEditPopover(e))}
            className={
              isCreator
                ? clsx(classes.editIcon, !isPaidPlan ? 'cursor-default' : null)
                : 'invisible'
            }
          >
            more_horiz
          </Icon>
        </Tooltip>
        <CommentReplyMenu
          isEditBoxOpen={isEditBoxOpen}
          anchorEl={anchorEl}
          onClose={editCommentBoxClose}
          onClickEdit={activeEditing}
          onClickDelete={activeDeleting}
        />
      </div>
      <div className={isEditing ? 'cursor-pointer flex justify-end' : 'hidden'}>
        {/*Submit Edit Button */}
        <Button
          size="small"
          color="secondary"
          disabled={isSubmitEditing || R.isEmpty(updatedText)}
          onClick={submitEditing}
          className={clsx(classes.submitEdit, 'md:hidden')}
        >
          <FormattedMessage id="notificationTimeline.card.comment.editSubmit.button" />
        </Button>

        <Button
          size="small"
          style={{
            fontSize: '1rem',
          }}
          onClick={cancelEditing}
        >
          <FormattedMessage id="action.cancel" />
        </Button>
      </div>
      {/* This is Dialog */}
      <Dialog
        open={isOpenDeletingDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
      >
        <DialogTitle color="secondary">
          <FormattedMessage id="notificationTimeline.card.comment.delete.dialog.title" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <FormattedMessage id="notificationTimeline.card.comment.delete.dialog.description" />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <FormattedMessage id="action.cancel" />
          </Button>
          <Button
            style={{ color: themesConfig.default.palette.textDanger.main }}
            onClick={handleDeleteItem}
            autoFocus
          >
            <FormattedMessage id="action.delete" />
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

ReplyCard.propTypes = {
  reply: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    creator: PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      avatar: PropTypes.string,
      type: PropTypes.number.isRequired,
    }),
  }),
  notificationId: PropTypes.number.isRequired,
  handleDeleteReply: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
}

export default ReplyCard
